import apiFetch from './apiFetch';

function appendPeriodParams(params, { month, startDate, endDate }) {
  if (month) {
    params.set('month', month);
  }

  if (startDate) {
    params.set('startDate', startDate);
  }

  if (endDate) {
    params.set('endDate', endDate);
  }
}

function buildMatrixPath(basePath, { month, startDate, endDate, customerNumbers }) {
  const params = new URLSearchParams();
  appendPeriodParams(params, { month, startDate, endDate });

  customerNumbers.forEach((customerNumber) => {
    params.append('customerNumbers', customerNumber);
  });

  return `${basePath}?${params.toString()}`;
}

function buildMatrixRequestBody({ month, startDate, endDate, customerNumbers }) {
  const body = {
    customerNumbers,
  };

  if (month) {
    body.month = month;
  }

  if (startDate) {
    body.startDate = startDate;
  }

  if (endDate) {
    body.endDate = endDate;
  }

  return body;
}

function createRequestError(status, message, payload = null) {
  const error = new Error(message);
  error.status = status;
  error.payload = payload;
  return error;
}

async function readResponsePayload(response) {
  const contentType = response.headers?.get?.('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getPayloadMessage(payload, fallbackMessage) {
  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }

    if (typeof payload.lastError === 'string' && payload.lastError.trim()) {
      return payload.lastError.trim();
    }
  }

  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }

  return fallbackMessage;
}

async function requestJson(path, options, fallbackMessage) {
  const response = await apiFetch(path, options);
  const payload = await readResponsePayload(response);

  if (!response.ok) {
    throw createRequestError(
      response.status,
      getPayloadMessage(payload, fallbackMessage),
      payload
    );
  }

  return payload;
}

async function requestMatrix(path, options, allowedStatuses, fallbackMessage) {
  const response = await apiFetch(path, options);
  const payload = await readResponsePayload(response);

  if (!allowedStatuses.has(response.status)) {
    throw createRequestError(
      response.status,
      getPayloadMessage(payload, fallbackMessage),
      payload
    );
  }

  return {
    status: response.status,
    data: payload,
  };
}

export function fetchCustomerOptions({ signal } = {}) {
  return requestJson(
    '/api/customers',
    { signal },
    'Failed to load customer options.'
  );
}

export function prepareCustomerOrderMatrix({
  month,
  startDate,
  endDate,
  customerNumbers,
  signal,
}) {
  return requestMatrix(
    '/api/customer-order-matrix/prepare',
    {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        buildMatrixRequestBody({
          month,
          startDate,
          endDate,
          customerNumbers,
        })
      ),
    },
    new Set([200, 202]),
    'Failed to prepare the customer report.'
  );
}

export function getCustomerOrderMatrixStatus({
  month,
  startDate,
  endDate,
  customerNumbers,
  signal,
}) {
  return requestMatrix(
    buildMatrixPath('/api/customer-order-matrix/status', {
      month,
      startDate,
      endDate,
      customerNumbers,
    }),
    { signal },
    new Set([200]),
    'Failed to load the customer report status.'
  );
}

export function getCustomerOrderMatrixResult({
  month,
  startDate,
  endDate,
  customerNumbers,
  signal,
}) {
  return requestMatrix(
    buildMatrixPath('/api/customer-order-matrix/result', {
      month,
      startDate,
      endDate,
      customerNumbers,
    }),
    { signal },
    new Set([200, 202, 404, 409]),
    'Failed to load the customer report.'
  );
}
